import React, { useState, useEffect } from 'react';
import { useStyletron } from 'baseui';
import { Button } from 'baseui/button';
import { Input } from 'baseui/input';
import { Modal, ModalHeader, ModalBody, ModalFooter } from 'baseui/modal';
import { Notification, KIND } from 'baseui/notification';
import { Room, UpdateRoomData } from './types';
import { roomApi } from './api';

interface EditRoomModalProps {
	isOpen: boolean;
	onClose: () => void;
	onSuccess: () => void;
	room: Room | null;
}

const LabeledInput: React.FC<{
	label: string;
	type?: string;
	value: string;
	onChange: (value: string) => void;
	placeholder?: string;
}> = ({ label, type = 'text', value, onChange, placeholder }) => {
	const [css] = useStyletron();
	return (
		<div className={css({ marginBottom: '0' })}>
			<label
				className={css({
					display: 'block',
					fontSize: '0.9rem',
					fontWeight: '600',
					color: '#374151',
					marginBottom: '8px'
				})}>
				{label}
			</label>
			<Input
				type={type}
				placeholder={placeholder || label}
				value={value}
				onChange={(e) => onChange(e.target.value)}
				overrides={{
					Root: {
						style: {
							marginBottom: '0',
							width: '100%'
						}
					}
				}}
			/>
		</div>
	);
};

const LabeledMultiSelect: React.FC<{
	label: string;
	options: Array<{ label: string; value: string }>;
	value: string[];
	onChange: (value: string[]) => void;
	placeholder?: string;
}> = ({ label, options, value, onChange, placeholder }) => {
	const [css] = useStyletron();
	const [isOpen, setIsOpen] = useState(false);

	const selectedLabels = options.filter((opt) => value.includes(opt.value)).map((opt) => opt.label);
	const displayText = selectedLabels.length > 0 ? selectedLabels.join(', ') : placeholder || `Select ${label}`;

	const toggleOption = (optValue: string) => {
		if (value.includes(optValue)) {
			onChange(value.filter((v) => v !== optValue));
		} else {
			onChange([...value, optValue]);
		}
	};

	return (
		<div className={css({ marginBottom: '0', position: 'relative' })}>
			<label
				className={css({
					display: 'block',
					fontSize: '0.9rem',
					fontWeight: '600',
					color: '#374151',
					marginBottom: '8px'
				})}>
				{label}
			</label>
			<div
				className={css({
					position: 'relative',
					width: '100%'
				})}>
				<button
					type='button'
					onClick={() => setIsOpen(!isOpen)}
					className={css({
						width: '100%',
						padding: '12px 16px',
						border: '1px solid #d1d5db',
						borderRadius: '8px',
						backgroundColor: '#ffffff',
						textAlign: 'left',
						fontSize: '0.9rem',
						color: selectedLabels.length > 0 ? '#374151' : '#9ca3af',
						cursor: 'pointer',
						display: 'flex',
						justifyContent: 'space-between',
						alignItems: 'center',
						':hover': {
							borderColor: '#9ca3af'
						},
						':focus': {
							outline: 'none',
							borderColor: '#3b82f6',
							boxShadow: '0 0 0 3px rgba(59, 130, 246, 0.1)'
						}
					})}>
					<span className={css({ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' })}>{displayText}</span>
					<span
						className={css({
							transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
							transition: 'transform 0.2s ease',
							fontSize: '0.8rem',
							color: '#6b7280'
						})}>
						▼
					</span>
				</button>

				{isOpen && (
					<div
						className={css({
							position: 'absolute',
							top: '100%',
							left: 0,
							right: 0,
							backgroundColor: '#ffffff',
							border: '1px solid #d1d5db',
							borderRadius: '8px',
							boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
							zIndex: 1000,
							marginTop: '4px'
						})}>
						{options.map((option) => (
							<button
								key={option.value}
								type='button'
								onClick={() => toggleOption(option.value)}
								className={css({
									width: '100%',
									padding: '12px 16px',
									border: 'none',
									backgroundColor: value.includes(option.value) ? '#e0f2fe' : '#ffffff',
									textAlign: 'left',
									fontSize: '0.9rem',
									color: '#374151',
									cursor: 'pointer',
									display: 'flex',
									alignItems: 'center',
									gap: '8px',
									':hover': {
										backgroundColor: value.includes(option.value) ? '#bae6fd' : '#f9fafb'
									},
									':first-child': {
										borderTopLeftRadius: '8px',
										borderTopRightRadius: '8px'
									},
									':last-child': {
										borderBottomLeftRadius: '8px',
										borderBottomRightRadius: '8px'
									}
								})}>
								<span
									className={css({
										width: '16px',
										height: '16px',
										border: value.includes(option.value) ? '2px solid #3b82f6' : '2px solid #d1d5db',
										borderRadius: '4px',
										backgroundColor: value.includes(option.value) ? '#3b82f6' : 'transparent',
										display: 'flex',
										alignItems: 'center',
										justifyContent: 'center',
										color: '#ffffff',
										fontSize: '0.7rem'
									})}>
									{value.includes(option.value) && '✓'}
								</span>
								{option.label}
							</button>
						))}
					</div>
				)}
			</div>

			{/* Overlay to close dropdown when clicking outside */}
			{isOpen && (
				<button
					type='button'
					className={css({
						position: 'fixed',
						top: 0,
						left: 0,
						right: 0,
						bottom: 0,
						zIndex: 999,
						border: 'none',
						background: 'transparent',
						cursor: 'default'
					})}
					onClick={() => setIsOpen(false)}
					aria-label='Close dropdown'
				/>
			)}
		</div>
	);
};

const EditRoomModal: React.FC<EditRoomModalProps> = ({ isOpen, onClose, onSuccess, room }) => {
	const [css] = useStyletron();
	const [loading, setLoading] = useState(false);
	const [notification, setNotification] = useState<{ message: string; kind: typeof KIND[keyof typeof KIND] } | null>(null);
	const [updateRoomData, setUpdateRoomData] = useState<UpdateRoomData>({
		_id: '',
		hostRoomId: '',
		hostId: '',
		hostName: '',
		title: '',
		tags: '',
		bio: '',
		priorityOrder: '',
		badgeIcon: '',
		badgeText: '',
		segmentNames: '',
		languages: [],
		charges: '',
		durationInMinutes: ''
	});

	const languageOptions = [
		{ label: 'Hindi', value: 'Hindi' },
		{ label: 'English', value: 'English' }
	];

	// Populate form when room data is available
	useEffect(() => {
		if (room) {
			setUpdateRoomData({
				_id: room._id || '',
				hostRoomId: room._id || '', // Use _id as hostRoomId
				hostId: room.hostId?.toString() || '',
				hostName: room.hostName || '',
				title: room.title || '',
				tags: room.tags?.join(', ') || '',
				bio: room.bio || '',
				priorityOrder: room.priorityOrder?.toString() || '',
				badgeIcon: room.badge?.icon || room.badge?.bgUrl || '',
				badgeText: room.badge?.text || '',
				segmentNames: room.segmentNames?.join(', ') || '',
				languages: room.languages || [],
				charges: room.charges?.toString() || '',
				durationInMinutes: room.durationInMinutes?.toString() || ''
			});
		}
	}, [room]);

	// Reset state when modal closes
	useEffect(() => {
		if (!isOpen) {
			setNotification(null);
			setUpdateRoomData({
				_id: '',
				hostRoomId: '',
				hostId: '',
				hostName: '',
				title: '',
				tags: '',
				bio: '',
				priorityOrder: '',
				badgeIcon: '',
				badgeText: '',
				segmentNames: '',
				languages: [],
				charges: '',
				durationInMinutes: ''
			});
		}
	}, [isOpen]);

	const handleUpdateRoom = async () => {
		setLoading(true);
		setNotification(null);
		try {
			const response = await roomApi.updateRoom(updateRoomData);
			if (response.success) {
				// Call success callback to refresh the list
				onSuccess();
				// Close modal immediately
				onClose();
			} else {
				setNotification({ message: response.error || 'Failed to update room', kind: KIND.negative });
			}
		} catch (error: any) {
			setNotification({ message: 'Error updating room', kind: KIND.negative });
		} finally {
			setLoading(false);
		}
	};

	const handleClose = () => {
		setNotification(null);
		// Reset form data when closing
		setUpdateRoomData({
			_id: '',
			hostRoomId: '',
			hostId: '',
			hostName: '',
			title: '',
			tags: '',
			bio: '',
			priorityOrder: '',
			badgeIcon: '',
			badgeText: '',
			segmentNames: '',
			languages: [],
			charges: '',
			durationInMinutes: ''
		});
		onClose();
	};

	if (!room) {
		return null;
	}

	return (
		<Modal
			isOpen={isOpen}
			onClose={handleClose}
			size='large'
			overrides={{
				Dialog: {
					style: {
						width: '90vw',
						maxWidth: '1200px',
						minWidth: '800px'
					}
				}
			}}>
			<ModalHeader>Edit Room</ModalHeader>
			<ModalBody>
				{/* Notifications */}
				{notification && (
					<div className={css({ marginBottom: '24px' })}>
						<Notification kind={notification.kind} onClose={() => setNotification(null)}>
							{notification.message}
						</Notification>
					</div>
				)}

				<div
					className={css({
						display: 'grid',
						gridTemplateColumns: 'repeat(3, 1fr)',
						gap: '20px',
						'@media (max-width: 1200px)': {
							gridTemplateColumns: 'repeat(2, 1fr)'
						},
						'@media (max-width: 768px)': {
							gridTemplateColumns: '1fr'
						}
					})}>
					<LabeledInput
						label='Host ID'
						type='text'
						value={updateRoomData.hostId}
						onChange={(value) => setUpdateRoomData({ ...updateRoomData, hostId: value })}
						placeholder='Enter host ID'
					/>
					<LabeledInput
						label='Host Name'
						value={updateRoomData.hostName}
						onChange={(value) => setUpdateRoomData({ ...updateRoomData, hostName: value })}
					/>
					<LabeledInput
						label='Room Title'
						value={updateRoomData.title}
						onChange={(value) => setUpdateRoomData({ ...updateRoomData, title: value })}
					/>
					<LabeledInput
						label='Tags (comma separated)'
						value={updateRoomData.tags}
						onChange={(value) => setUpdateRoomData({ ...updateRoomData, tags: value })}
					/>
					<LabeledInput
						label='Priority Order'
						type='number'
						value={updateRoomData.priorityOrder}
						onChange={(value) => setUpdateRoomData({ ...updateRoomData, priorityOrder: value })}
						placeholder='Enter priority order'
					/>
					<LabeledInput
						label='Badge Icon'
						value={updateRoomData.badgeIcon}
						onChange={(value) => setUpdateRoomData({ ...updateRoomData, badgeIcon: value })}
						placeholder='Enter badge icon'
					/>
					<LabeledInput
						label='Badge Text'
						value={updateRoomData.badgeText}
						onChange={(value) => setUpdateRoomData({ ...updateRoomData, badgeText: value })}
						placeholder='Enter badge text'
					/>
					<LabeledInput
						label='Bio'
						value={updateRoomData.bio}
						onChange={(value) => setUpdateRoomData({ ...updateRoomData, bio: value })}
					/>
					<LabeledInput
						label='Segment Names (comma separated)'
						value={updateRoomData.segmentNames}
						onChange={(value) => setUpdateRoomData({ ...updateRoomData, segmentNames: value })}
						placeholder='e.g., VIP, Premium, Standard'
					/>
					<LabeledMultiSelect
						label='Languages'
						options={languageOptions}
						value={updateRoomData.languages}
						onChange={(value) => setUpdateRoomData({ ...updateRoomData, languages: value })}
						placeholder='Select Languages'
					/>
					<LabeledInput
						label='Charges'
						type='number'
						value={updateRoomData.charges}
						onChange={(value) => setUpdateRoomData({ ...updateRoomData, charges: value })}
						placeholder='Enter charges amount'
					/>
					<LabeledInput
						label='Duration (Minutes)'
						type='number'
						value={updateRoomData.durationInMinutes}
						onChange={(value) => setUpdateRoomData({ ...updateRoomData, durationInMinutes: value })}
						placeholder='Enter duration in minutes'
					/>
				</div>
			</ModalBody>
			<ModalFooter>
				<div className={css({ display: 'flex', gap: '12px', width: '100%' })}>
					<Button kind='tertiary' onClick={handleClose} disabled={loading}>
						Cancel
					</Button>
					<Button onClick={handleUpdateRoom} isLoading={loading} disabled={loading}>
						Update Room
					</Button>
				</div>
			</ModalFooter>
		</Modal>
	);
};

export default EditRoomModal;
